<template>
    <b-modal :visible="value" @change="onVisibleChange" title="Post Adjustment" hide-footer>
        <b-alert :show="reasonCodesUnavailable" variant="warning" class="py-2">
            An adjustment requires a reason code, but no Statement Reason / Credit Codes are
            configured. An administrator must add them under Configuration &gt; AR / Patient Billing.
        </b-alert>

        <b-row>
            <b-col cols="6">
                <b-form-group label="Type">
                    <b-select v-model.number="form.transactionType" :options="adjustmentTypeOptions" />
                </b-form-group>
            </b-col>
            <b-col cols="6">
                <b-form-group label="Direction">
                    <b-select v-model="form.direction" :options="directionOptions" />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col cols="6">
                <b-form-group label="Amount">
                    <b-form-input v-model.number="form.amount" type="number" min="0" step="0.01" />
                </b-form-group>
            </b-col>
            <b-col cols="6">
                <b-form-group :label="reasonCodeLabel">
                    <b-select v-if="useReasonCodeList" v-model="form.reasonCode" :options="reasonCodeOptions"
                        :disabled="reasonCodesUnavailable" />
                    <b-form-input v-else v-model="form.reasonCode" />
                </b-form-group>
            </b-col>
        </b-row>
        <b-form-group label="Notes">
            <b-form-input v-model="form.notes" />
        </b-form-group>
        <small class="text-muted">A charge increases the balance owed; a credit reduces it.</small>

        <div class="text-right mt-2">
            <b-button variant="secondary" class="mr-2" :disabled="saving" @click="close">Cancel</b-button>
            <b-button variant="primary"
                :disabled="saving || reasonCodesUnavailable || !(form.amount > 0) || !form.reasonCode"
                @click="postAdjustment">
                Post Adjustment
            </b-button>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import { ARAdjustmentTypeOptions } from "@/models/AR";
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import ARReasonCodesMixin from "@/mixins/ARReasonCodesMixin";

@Component({
    name: "ARPostAdjustmentModal",
})
export default class ARPostAdjustmentModal extends Mixins(ARReasonCodesMixin) {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ default: null }) accountId!: number | null;

    private saving = false;
    private adjustmentTypeOptions = ARAdjustmentTypeOptions;
    private directionOptions = [
        { value: "charge", text: "Charge (increases balance)" },
        { value: "credit", text: "Credit (reduces balance)" },
    ];
    private form = this.emptyForm();

    @Watch("value")
    async onShow(visible: boolean): Promise<void> {
        if (!visible) return;
        this.form = this.emptyForm();
        await this.loadReasonCodeConfiguration();
        this.form.reasonCode = this.knownReasonCode(this.form.reasonCode);
    }

    private emptyForm() {
        return { transactionType: 4, direction: "charge", amount: 0, reasonCode: "", notes: "" };
    }

    // Direction is a display-only convenience: the ledger stores a signed amount, where a positive
    // amount is a charge and a negative one a credit, so the sign is applied here and the direction
    // itself is never sent.
    buildPayload() {
        return {
            transactionType: this.form.transactionType,
            amount: this.form.direction === "credit"
                ? -Math.abs(this.form.amount)
                : Math.abs(this.form.amount),
            reasonCode: this.form.reasonCode,
            notes: this.form.notes,
        };
    }

    async postAdjustment(): Promise<void> {
        if (!this.accountId) return;
        try {
            this.saving = true;
            await axios.post(`/AR/accounts/${this.accountId}/adjustments`, this.buildPayload());
            this.$notification(NotificationOptions.success("Adjustment posted."));
            this.$emit("saved");
            this.close();
        } catch (error) {
            this.$notification(NotificationOptions.error(error));
        } finally {
            this.saving = false;
        }
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }

    close(): void {
        this.$emit("input", false);
    }
}
</script>

<style scoped></style>
